import Option from "@components/systemAdmin/Option";
import { getBackgroundColour, getTextColour } from "@helpers/colours";
import _ from "lodash";
import React from "react";
import { Dimmer, Icon, Loader, Table } from "semantic-ui-react";

class DataGrid extends React.Component {
  state = {
    activeRow: null,
    sortBy: null,
    data: this.props.data,
    direction: "ascending",
  };

  componentDidUpdate = (prevProps) => {
    if (prevProps.data !== this.props.data) {
      this.setState({
        activeRow: null,
        sortBy: null,
        direction: "ascending",
        data: this.props.data,
      });
    }
  };

  onRowClick = (row) => {
    if (!this.props.disableActiveRow && row.isClickable !== false) {
      this.setState({ activeRow: row }, () => {
        if (typeof this.props.onRowClick === "function") {
          this.props.onRowClick(row);
        }
      });
    }
  };

  onCellClick = (cell) => {
    if (
      typeof this.props.onCellClick === "function" &&
      this.props.disableActiveRow
    ) {
      this.props.onCellClick(cell);
    }
  };

  renderData() {
    return this.state.data.map((data, index) => {
      const activeRow = this.props.activeRow || this.state.activeRow;
      const jsonData = JSON.stringify(data);
      const isActiveRow = Array.isArray(this.props.activeRow)
        ? this.props.activeRow.map((x) => JSON.stringify(x)).includes(jsonData)
        : JSON.stringify(activeRow) === jsonData;

      const rowDef = this.props.rowDef?.[index];
      return (
        <Table.Row
          className={
            !this.props.disableActiveRow &&
            data.isClickable !== false &&
            "clickable"
          }
          style={{
            position: rowDef?.locked ? "sticky" : "static",
            top: rowDef?.top,
            ...(isActiveRow && {
              border: "2px solid #2185d0",
              boxSizing: "border-box",
            }),
          }}
          active={isActiveRow}
          key={`row_${index}`}
          onClick={() => this.onRowClick(data)}
        >
          {this.dataToRow(data, index, isActiveRow)}
        </Table.Row>
      );
    });
  }

  handleSort = (sortBy) => {
    let isString = false;

    for (const o of this.state.data) {
      if (Number.isNaN(parseFloat(o.cellData[sortBy].data.actualValue))) {
        isString = true;
        break;
      }
    }

    const sorted = _.orderBy(
      [...this.state.data],
      function (o) {
        return !isString
          ? parseFloat(o.cellData[sortBy].data.actualValue)
          : o.cellData[sortBy].data.actualValue;
      },
      this.state.direction === "ascending" ? "desc" : "asc",
    );

    this.setState({
      direction:
        this.state.direction === "ascending" ? "descending" : "ascending",
      sortBy,
      data: sorted,
    });
  };

  getCellAlignment = (index) => {
    const textAlign = ["left", "center", "right"];

    if (!this.props.rowDef[index]) {
      return textAlign[0];
    } else {
      const alignment = textAlign[this.props.rowDef[index].alignment];
      return alignment || textAlign[0];
    }
  };

  getHeaderAlignment = (index) => {
    const textAlign = ["left", "center", "right"];
    return textAlign[index];
  };

  dataToRow(data, rowIndex, isActiveRow) {
    let left = 0;
    return data?.cellData
      ?.filter((item) => !item.hide)
      ?.map((item, index) => {
        const lock = this.props.headings[index]?.lock ?? "false";
        const width = this.props.headings[index]?.width ?? 0;
        switch (item.type) {
          case "cell":
            left += width;
            const defaultStyles = {
              color: getTextColour(item.data),
              position: lock ? "sticky" : "static",
              left: left - width,
              minWidth: `${width}px`,
            };
            const style = {
              ...defaultStyles,
              background: getBackgroundColour(item.data),
            };
            return (
              <Table.Cell
                as={item.data.lock ? Table.HeaderCell : Table.Cell}
                textAlign={this.getCellAlignment(index)}
                key={`cell_${rowIndex}_${index}`}
                singleLine={true}
                style={style}
                onClick={() => this.onCellClick(item.data)}
                className={item.data.bold ? "font-bold" : ""}
              >
                {item.data.displayValue !== "" ? item.data.displayValue : " "}
              </Table.Cell>
            );
          default:
            return (
              <Table.Cell
                textAlign={this.getCellAlignment(index)}
                key={item.name}
                singleLine={true}
              >
                <Option
                  option={item.data}
                  onChange={this.props.onOptionChange}
                />
              </Table.Cell>
            );
        }
      });
  }

  renderGroupHeadings() {
    return this.props.groupHeadings.map((data, index) => {
      return (
        <Table.HeaderCell
          textAlign={this.getHeaderAlignment(data.alignment)}
          style={{
            background: getBackgroundColour(data, "#F2F2F2"),
            color: getTextColour(data),
            cursor: "auto",
          }}
          colSpan={data.cols}
        >
          {data.header}
        </Table.HeaderCell>
      );
    });
  }

  renderHeadings() {
    let left = 0;
    return this.props.headings.map((data, index) => {
      left += data.width;
      return (
        <Table.HeaderCell
          key={`header_${index}`}
          textAlign={this.getHeaderAlignment(data.alignment)}
          style={{
            background: getBackgroundColour(data, "#F2F2F2"),
            color: getTextColour(data),
            cursor: "auto",
            position: data.lock ? "sticky" : "static",
            left: left - data.width,
            minWidth: data.lock ? `${data.width}px` : "auto",
          }}
          sorted={index === this.state.sortBy ? this.state.direction : null}
          onClick={() => !this.props.disableSort && this.handleSort(index)}
        >
          {data.text}
        </Table.HeaderCell>
      );
    });
  }

  render() {
    let classes = "table-overflow max-height";

    if (this.props.hideOverflow) {
      classes = "";
    }

    if (this.props.stretched) {
      classes = "table-overflow flex-stretch";
    }

    if (this.props.fullHeight) {
      classes = "table-overflow flex-stretch-vertical";
    }

    if (this.props.className) {
      classes += " " + this.props.className;
    }

    if (this.props.loading) {
      return (
        <div style={this.props.style} className={classes}>
          <Dimmer active inverted>
            <Loader inverted>Loading</Loader>
          </Dimmer>
        </div>
      );
    }

    if (!this.state.data || !this.state.data.length) {
      return (
        <div style={this.props.style} className={classes}>
          <div style={{ marginTop: "20px", textAlign: "center" }}>
            {" "}
            <Icon name="dont" size="large" disabled />{" "}
            {this.props.noResults
              ? this.props.noResults
              : "There are no results for the current set of filters."}
          </div>
        </div>
      );
    }

    return (
      <div style={this.props.style} className={classes} >
        <Table
          sortable={!this.props.disableSort}
          unstackable
          {...(!this.props.hideOverflow ? { basic: "very" } : {})}
          compact
          structured={this.props.structured || true}
          celled={this.props.celled || true}
        >
          {this.props.headings && (
            <Table.Header
              unstackable
              style={{
                position: this.props.hideOverflow ? "static" : "sticky",
              }}
            >
              {this.props.groupHeadings && (
                <Table.Row>{this.renderGroupHeadings()}</Table.Row>
              )}
              <Table.Row unstackale>{this.renderHeadings()}</Table.Row>
            </Table.Header>
          )}
          <Table.Body>{this.renderData()}</Table.Body>
        </Table>
      </div>
    );
  }
}

export default DataGrid;
